import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { AppShell } from './components/AppShell';
import { CookieConsent } from './components/CookieConsent';
import { Empty, Loading } from './components/ui';
import { DecisionPage } from './features/decision/DecisionPage';
import { LoginPage, RegisterPage, WelcomePage } from './features/auth/AuthPages';
import { useAuth } from './features/auth/AuthProvider';
import type { Role } from './types/trade';

// Secondary pages are code-split; the decision view and sign-in pages are in the main bundle.
const SensitivityPage = lazy(() => import('./features/sensitivity/SensitivityPage'));
const UncertaintyPage = lazy(() => import('./features/uncertainty/UncertaintyPage'));
const AssumptionsPage = lazy(() => import('./features/assumptions/AssumptionsPage'));
const ScenariosPage = lazy(() => import('./features/scenarios/ScenariosPage'));
const AdvisorPage = lazy(() => import('./features/advisor/AdvisorPage'));
const SettingsPage = lazy(() => import('./features/settings/SettingsPage'));
const ManualPage = lazy(() => import('./features/manual/ManualPage'));
const TradeDashboardPage = lazy(() => import('./features/trade/DashboardPage'));
const ProfilePage = lazy(() => import('./features/trade/ProfilePage'));
const DirectoryPage = lazy(() => import('./features/trade/DirectoryPage'));
const OrgDetailPage = lazy(() => import('./features/trade/DirectoryPage').then((m) => ({ default: m.OrgDetailPage })));
const MyListingsPage = lazy(() => import('./features/trade/OffersPages').then((m) => ({ default: m.MyListingsPage })));
const MyRequirementsPage = lazy(() => import('./features/trade/OffersPages').then((m) => ({ default: m.MyRequirementsPage })));
const MarketPage = lazy(() => import('./features/trade/OffersPages').then((m) => ({ default: m.MarketPage })));
const EnquiriesPage = lazy(() => import('./features/trade/EnquiriesPage'));
const EnquiryThreadPage = lazy(() => import('./features/trade/EnquiriesPage').then((m) => ({ default: m.EnquiryThreadPage })));
const CatalogPage = lazy(() => import('./features/trade/CatalogPage'));
const CorridorPage = lazy(() => import('./features/trade/CorridorPage'));
const IntelligencePage = lazy(() => import('./features/trade/IntelligencePage'));
const PortsPage = lazy(() => import('./features/trade/PortsPage'));
const DiscoveryPage = lazy(() => import('./features/trade/DiscoveryPage'));
const ReviewPage = lazy(() => import('./features/trade/ReviewPage'));
const UsersPage = lazy(() => import('./features/trade/AdminPages').then((m) => ({ default: m.UsersPage })));
const NewOrgPage = lazy(() => import('./features/trade/AdminPages').then((m) => ({ default: m.NewOrgPage })));
const AccountPage = lazy(() => import('./features/trade/AdminPages').then((m) => ({ default: m.AccountPage })));
const LegalPage = lazy(() => import('./features/legal/LegalPage'));
const BillingPage = lazy(() => import('./features/billing/BillingPage'));
const BillingAdminPage = lazy(() => import('./features/billing/BillingAdminPage'));

const STAFF: Role[] = ['super_admin', 'admin', 'analyst'];
const TRADE_ADMIN: Role[] = ['super_admin', 'admin'];

function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<Loading label="Loading…" />}>{children}</Suspense>;
}

/** Signed-in accounts only (optionally limited to roles). Anonymous visitors go to the portal chooser. */
function RequireUser({ roles }: { roles?: Role[] }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Loading label="Checking your session…" />;
  if (!user) return <Navigate to="/welcome" replace state={{ from: location.pathname + location.search }} />;
  if (roles && !roles.includes(user.role)) return <Empty title="Not available for your account">This area is for {roles.map((r) => r.replace('_', ' ')).join(', ')} accounts.</Empty>;
  return <Lazy><Outlet /></Lazy>;
}

/** Decision-engine pages: staff, or anyone when the server runs ENGINE_ACCESS=open. Buyers and sellers go to their workspace. */
function EngineGate() {
  const { user, loading, engineAllowed, engineEnabled } = useAuth();
  const location = useLocation();
  if (loading) return <Loading label="Checking your session…" />;
  if (engineAllowed) return <Lazy><Outlet /></Lazy>;
  if (user) return <Navigate to="/trade" replace />;
  if (!engineEnabled) return <Navigate to="/welcome" replace />;
  return <Navigate to="/login/staff" replace state={{ from: location.pathname }} />;
}

export function App() {
  return (
    <>
    <CookieConsent />
    <Routes>
      <Route path="welcome" element={<WelcomePage />} />
      <Route path="legal" element={<Navigate to="/legal/terms" replace />} />
      <Route path="legal/:doc" element={<Lazy><LegalPage /></Lazy>} />
      <Route path="login" element={<Navigate to="/welcome" replace />} />
      <Route path="login/:portal" element={<LoginPage />} />
      <Route path="register/:role" element={<RegisterPage />} />
      <Route element={<AppShell />}>
        <Route element={<EngineGate />}>
          <Route index element={<DecisionPage />} />
          <Route path="sensitivity" element={<SensitivityPage />} />
          <Route path="uncertainty" element={<UncertaintyPage />} />
          <Route path="assumptions" element={<AssumptionsPage />} />
          <Route path="scenarios" element={<ScenariosPage />} />
          <Route path="advisor" element={<AdvisorPage />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>
        <Route path="manual" element={<Lazy><ManualPage /></Lazy>} />
        <Route element={<RequireUser />}>
          <Route path="trade" element={<TradeDashboardPage />} />
          <Route path="trade/directory" element={<DirectoryPage />} />
          <Route path="trade/orgs/:orgId" element={<OrgDetailPage />} />
          <Route path="trade/market" element={<MarketPage />} />
          <Route path="trade/enquiries" element={<EnquiriesPage />} />
          <Route path="trade/enquiries/:enquiryId" element={<EnquiryThreadPage />} />
          <Route path="trade/catalog" element={<CatalogPage />} />
          <Route path="trade/corridor" element={<CorridorPage />} />
          <Route path="trade/intelligence" element={<IntelligencePage />} />
          <Route path="trade/ports" element={<PortsPage />} />
          <Route path="account" element={<AccountPage />} />
        </Route>
        <Route element={<RequireUser roles={['buyer', 'seller', 'super_admin']} />}>
          <Route path="trade/profile" element={<ProfilePage />} />
          <Route path="billing" element={<BillingPage />} />
        </Route>
        <Route element={<RequireUser roles={['seller', 'super_admin']} />}>
          <Route path="trade/listings" element={<MyListingsPage />} />
        </Route>
        <Route element={<RequireUser roles={['buyer', 'super_admin']} />}>
          <Route path="trade/requirements" element={<MyRequirementsPage />} />
        </Route>
        <Route element={<RequireUser roles={STAFF} />}>
          <Route path="trade/discovery" element={<DiscoveryPage />} />
          <Route path="trade/review" element={<ReviewPage />} />
        </Route>
        <Route element={<RequireUser roles={TRADE_ADMIN} />}>
          <Route path="trade/orgs/new" element={<NewOrgPage />} />
          <Route path="trade/orgs/:orgId/edit" element={<ProfilePage />} />
        </Route>
        <Route element={<RequireUser roles={['super_admin']} />}>
          <Route path="admin/users" element={<UsersPage />} />
          <Route path="admin/billing" element={<BillingAdminPage />} />
        </Route>
        <Route path="*" element={<Empty title="Page not found">The page you requested does not exist.</Empty>} />
      </Route>
    </Routes>
    </>
  );
}
